import cloneDeep from 'lodash/cloneDeep'
import React from 'react'
import * as Yup from 'yup'

import { IconEyeExclamation } from '@tabler/icons-react'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { InfoTooltip } from '@/components/tooltips/InfoTooltip'

export const PERMISSION_LABELS = {
  cloud_access: 'Balto Cloud Access',
  edit_config: 'Playbook Management',
  edit_users: 'User Management',
  edit_leaderboard: 'Leaderboard Management',
  edit_qa: 'QA Access',
  edit_qa_copilot: 'Quality Access',
  realtime_coaching_access: 'Real-Time Coaching Access',
  midcall_playbook_switching_enabled: 'Midcall Playbook Switching',
  hierarchy_manager: 'Hierarchy Manager',
  prompt_editor: 'Prompt Editor',
  outbound_campaign_editor: 'Outbound Campaign Editor',
  togo_editor: 'Togo Editor',
  webhook_editor: 'Webhook Editor',
  verified_answer_editor: 'AgentGPT Editor',
}

// summary-service can't submit a complaint to NewRez without the agent's
// voip_user_id (stored on the user as `code`), so complaints on without one is
// a misconfiguration.
export const COMPLAINTS_REQUIRES_VOIP_USER_ID_MESSAGE =
  'VoIP User ID is required when Complaints Enabled is on'

export const cloudAccessSchema = {
  name: 'access',
  type: 'multiSelect',
  condenseOptions: false,
  showFullOptions: true,
  label: 'Balto Cloud Access',
  options: Object.entries(PERMISSION_LABELS).map(([value, label]) => ({ label, value })),
}

export const buildSchemaForUserForm = (
  playbooks,
  qaEnabled,
  qaCopilotEnabled,
  realtimeCoachingAccessEnabled,
  tagCategories,
  tags,
  orgDesktopEnterpriseEnabled,
  flags,
  isBaltoAdmin,
  isHierarchyManager,
  agentGPTEnabled,
  organizationId,
  toolsEnabled,
  outboundCampaignsEnabled,
  togoEnabled,
  webhooksEnabled,
  screenCaptureAccessEnabled,
  complaintsAlreadyEnabled = false
) => {
  const newCloudAccessSchema = cloneDeep(cloudAccessSchema)
  const playbookOptions = playbooks.map((playbook) => ({
    value: playbook.cid,
    label: playbook.name,
    isHidden: playbook.config_agent_app_visibility === false,
  }))

  const userDetailsSchema = [
    { name: 'first_name', type: 'text', label: 'First Name', required: true },
    { name: 'last_name', type: 'text', label: 'Last Name', required: true },
    { name: 'username', type: 'text', label: 'Email', required: true },
    {
      name: 'config_cid',
      type: 'select',
      label: 'Playbook',
      required: true,
      options: playbookOptions,
      formatOptionLabel: (option, { context }) => {
        const isDropdownValue = context === 'value'
        const popupPlacement = isDropdownValue ? 'top' : 'right'
        const isHiddenFromAgent = option.isHidden

        return (
          <div className={`playbook-option-wrapper${isHiddenFromAgent ? ' option-hidden' : ''}`}>
            <span>{option.label}</span>
            {isHiddenFromAgent && (
              <InteractiveTooltip
                content="This playbook is invisible to the agent. To change the visibility settings, please navigate to the Playbooks page"
                placement={popupPlacement}
              >
                <span
                  className="playbook-option-icon-wrapper"
                  data-testid={`playbook-hidden-icon-${option.value}`}
                >
                  <IconEyeExclamation className="icon-svg status-warning" stroke={2} />
                </span>
              </InteractiveTooltip>
            )}
          </div>
        )
      },
    },
    {
      name: 'deleted',
      type: 'select',
      label: 'Agent Status',
      required: true,
      options: [
        { value: false, label: 'Active' },
        { value: true, label: 'Deactivated' },
      ],
    },
  ]

  if (orgDesktopEnterpriseEnabled) {
    const enterpriseSchema = {
      name: 'desktop_enterprise_enabled',
      type: 'select',
      label: 'Desktop Mode',
      options: [
        { value: false, label: 'Balto Classic' },
        { value: true, label: 'Balto Enterprise' },
      ],
    }
    userDetailsSchema.push(enterpriseSchema)
  }

  if (isBaltoAdmin) {
    const aiAgentSchema = {
      name: 'ai_agent',
      label: 'Mark as AI Agent',
      type: 'checkbox',
      toggle: true,
    }
    userDetailsSchema.push(aiAgentSchema)

    const manageSupportAccessSchema = {
      name: 'manage_support_access',
      label: 'Manage Balto Support Access',
      type: 'checkbox',
      toggle: true,
    }
    userDetailsSchema.push(manageSupportAccessSchema)
  }

  if (flags?.cloudTopAgentToggle2022) {
    const topAgentSchema = {
      name: 'is_top_agent',
      label: 'Enable Content Customization',
      type: 'checkbox',
      toggle: true,
    }
    userDetailsSchema.push(topAgentSchema)
  }

  if (agentGPTEnabled) {
    const agentGPTSchema = {
      name: 'agent_gpt_access',
      label: 'Enable Agent GPT Access',
      type: 'checkbox',
      toggle: true,
    }
    userDetailsSchema.push(agentGPTSchema)
  }

  if (toolsEnabled) {
    const toolsEditorSchema = {
      name: 'tools_editor',
      label: 'Enable Tools Editor Access',
      type: 'checkbox',
      toggle: true,
    }
    userDetailsSchema.push(toolsEditorSchema)
  }

  if (organizationId === 2970) {
    const newrezComplaintsSchema = {
      name: 'newrez_complaints_enabled',
      label: 'Complaints Enabled',
      type: 'checkbox',
      toggle: true,
    }
    userDetailsSchema.push(newrezComplaintsSchema)
  }

  const tagSchema = tagCategories.map((category) => {
    return {
      name: category.name.toLowerCase(),
      type: 'select',
      label: category.name,
      options: tags
        .filter((tag) => tag.tag_category_id === category.id)
        .map((tag) => ({
          value: tag.id,
          label: tag.name,
        })),
    }
  })

  const screenCaptureSchema = {
    name: 'screen_capture_enabled',
    type: 'select',
    label: 'Screen Capture',
    nullable: true,
    defaultValue: null,
    options: [
      {
        value: null,
        label: 'Organization Default',
      },
      {
        value: true,
        label: 'True',
      },
      {
        value: false,
        label: 'False',
      },
    ],
  }
  // RTG3-878: the per-user control depends on the org holding the Screen
  // Capture Access entitlement — the backend rejects the write without it, for
  // every caller including Balto admins.
  //
  // Customer admins cannot grant the entitlement themselves, so for them the
  // field is simply absent. Balto admins can, so showing them nothing would
  // hide a two-step flow behind no affordance at all; they get the control
  // disabled with a tooltip pointing at the org's Flags page instead.
  if (screenCaptureAccessEnabled) {
    userDetailsSchema.push(screenCaptureSchema)
  } else if (isBaltoAdmin) {
    userDetailsSchema.push({
      ...screenCaptureSchema,
      disabled: true,
      label: (
        <span className="flex-align-center small-gap">
          Screen Capture
          <InfoTooltip
            content="Screen Capture Access is not enabled for this organization. Turn it on from the organization's Flags page before setting this per user."
            label="Why Screen Capture is disabled"
          />
        </span>
      ),
    })
  }

  // Kodi access is a tri-state, not a checkbox: null defers to the org's
  // `kodi_enabled` default, so an org can turn Kodi off for everyone and opt a
  // select few back in. Gated on the same LD flag that gates the Kodi nav item,
  // so orgs it hasn't been released to don't see a control for it.
  if (flags?.baltoCoworkEnabled) {
    const kodiAccessSchema = {
      name: 'kodi_enabled',
      type: 'select',
      label: 'Kodi Access',
      nullable: true,
      defaultValue: null,
      options: [
        {
          value: null,
          label: 'Organization Default',
        },
        {
          value: true,
          label: 'Enabled',
        },
        {
          value: false,
          label: 'Disabled',
        },
      ],
    }
    userDetailsSchema.push(kodiAccessSchema)
  }

  const uncategorizedTagsSchema =
    tags.length > 0
      ? [
          {
            name: 'uncategorizedTags',
            type: 'multiSelect',
            condenseOptions: false,
            label: 'Uncategorized Tags',
            options: tags
              .filter((tag) => tag.tag_category_id == null)
              .map((tag) => ({
                value: tag.id,
                label: tag.name,
              })),
          },
        ]
      : []

  if (!qaEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'edit_qa'
    )
  }

  if (!qaCopilotEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'edit_qa_copilot'
    )
  }

  if (!isBaltoAdmin) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'prompt_editor'
    )
  }

  if (!realtimeCoachingAccessEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'realtime_coaching_access'
    )
  }

  if (!isHierarchyManager && !isBaltoAdmin) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'hierarchy_manager'
    )
  }

  if (!outboundCampaignsEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'outbound_campaign_editor'
    )
  }

  if (!togoEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'togo_editor'
    )
  }

  // Service rejects it until the org is entitled — hide rather than offer a no-op.
  if (!webhooksEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'webhook_editor'
    )
  }

  // Verified answers live in AgentGPT, so an org without it has nothing to grant.
  if (!agentGPTEnabled) {
    newCloudAccessSchema.options = newCloudAccessSchema.options.filter(
      (option) => option.value !== 'verified_answer_editor'
    )
  }

  const voipSchema = [
    {
      name: 'code',
      type: 'text',
      label: 'VoIP User ID',
      // Only enforced where the Complaints toggle is rendered, so a stale
      // newrez_complaints_enabled value can't block saves in other orgs, and
      // only when complaints is being turned on: a user saved with it on and no
      // ID can still get unrelated edits (e.g. deactivation).
      ...(organizationId === 2970 &&
        !complaintsAlreadyEnabled && {
          customValidators: [
            Yup.string().when('newrez_complaints_enabled', {
              is: true,
              then: Yup.string().trim().required(COMPLAINTS_REQUIRES_VOIP_USER_ID_MESSAGE),
            }),
          ],
        }),
    },
  ]

  const externalSystemSchema = [
    {
      name: 'external_system_id',
      type: 'text',
      label: 'External System ID',
    },
  ]

  return [
    ...userDetailsSchema,
    ...[newCloudAccessSchema],
    ...tagSchema,
    ...uncategorizedTagsSchema,
    ...voipSchema,
    ...externalSystemSchema,
  ]
}

export const buildExistingValuesForUserForm = (user, tagCategories) => {
  const existingUserValues = {
    first_name: user.first_name,
    last_name: user.last_name,
    is_top_agent: user.is_top_agent,
    username: user.username,
    config_cid: user.config_cid,
    deleted: user.deleted,
    code: user.code || '',
    external_system_id: user.external_system_id || '',
    desktop_enterprise_enabled: user.desktop_enterprise_enabled,
    screen_capture_enabled: user.screen_capture_enabled,
    kodi_enabled: user.kodi_enabled ?? null,
    agent_gpt_access: user.agent_gpt_access ?? false,
    newrez_complaints_enabled: user.newrez_complaints_enabled ?? false,
    tools_editor: user.tools_editor ?? false,
    ai_agent: user.ai_agent ?? false,
    manage_support_access: user.manage_support_access ?? false,
  }

  const existingTagValues = user.tags
    .filter((tag) => tag.tag_category_id != null)
    .map((tag) => {
      const tagCategory = tagCategories.find((category) => category.id === tag.tag_category_id)
      const existingTag = {}
      existingTag[tagCategory.name.toLowerCase()] = tag.id

      return existingTag
    })

  const existingUncategorizedTags = {
    uncategorizedTags: user.tags
      .filter((tag) => tag.tag_category_id == null)
      .map((tag) => ({ value: tag.id, label: tag.name })),
  }

  const existingCloudAccess = {
    access: cloudAccessSchema.options.filter((option) => {
      if (option.value === 'cloud_access') {
        return user.password ? option : null
      }
      return user[option.value] ? option : null
    }),
  }

  let existingValues = {
    ...existingUserValues,
    ...existingUncategorizedTags,
    ...existingCloudAccess,
  }

  existingValues = Object.assign({}, existingValues, ...existingTagValues)

  return existingValues
}
